import React, { useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  TextField,
  Button,
  Paper,
  Grid,
  Chip,
  IconButton,
  Alert,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Language as LanguageIcon,
  Extension as ExtensionIcon,
  Image as ImageIcon,
  Palette as PaletteIcon
} from '@mui/icons-material';

const WebsiteRequirementsForm = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    cms: '',
    languages: ['English'],
    deeplIntegration: false,
    features: {
      homePage: true,
      aboutPage: false,
      services: false,
      products: false,
      contactPage: false,
      newsletter: false,
      socialMedia: false
    },
    otherFeatures: [],
    designInfo: '',
    designFiles: [],
    imagePreference: 'ai-generated',
    imageInstructions: ''
  });

  const [newFeature, setNewFeature] = useState('');
  const [errors, setErrors] = useState({});

  const cmsOptions = [
    { value: 'drupal', label: 'Drupal' },
    { value: 'wordpress', label: 'WordPress' },
    { value: 'joomla', label: 'Joomla' },
    { value: 'no-preference', label: 'I have no preference' }
  ];

  const languageOptions = [
    'English', 'Spanish', 'French', 'German', 'Italian', 
    'Portuguese', 'Dutch', 'Russian', 'Chinese', 'Japanese',
    'Arabic', 'Hindi', 'Korean', 'Polish', 'Turkish'
  ];

  const featureOptions = [
    { key: 'homePage', label: 'Home page' },
    { key: 'aboutPage', label: 'About us page' },
    { key: 'services', label: 'Services' },
    { key: 'products', label: 'Products' },
    { key: 'contactPage', label: 'Contact us page' },
    { key: 'newsletter', label: 'Newsletter subscription' },
    { key: 'socialMedia', label: 'Social media integration' }
  ];

  const handleCmsChange = (event) => {
    setFormData({ ...formData, cms: event.target.value });
    setErrors({ ...errors, cms: '' });
  };

  const handleLanguageChange = (event) => {
    setFormData({ ...formData, languages: event.target.value });
  };

  const handleDeeplChange = (event) => {
    setFormData({ ...formData, deeplIntegration: event.target.checked });
  };

  const handleFeatureChange = (feature) => (event) => {
    setFormData({
      ...formData,
      features: {
        ...formData.features,
        [feature]: event.target.checked
      }
    });
  };

  const handleAddOtherFeature = () => {
    if (newFeature.trim()) {
      setFormData({
        ...formData,
        otherFeatures: [...formData.otherFeatures, newFeature.trim()]
      });
      setNewFeature('');
    }
  };

  const handleRemoveOtherFeature = (index) => {
    setFormData({
      ...formData,
      otherFeatures: formData.otherFeatures.filter((_, i) => i !== index)
    });
  };

  const handleDesignInfoChange = (event) => {
    setFormData({ ...formData, designInfo: event.target.value });
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    setFormData({ ...formData, designFiles: [...formData.designFiles, ...files] });
  };

  const handleImagePreferenceChange = (event) => {
    setFormData({ ...formData, imagePreference: event.target.value });
  };

  const handleImageInstructionsChange = (event) => {
    setFormData({ ...formData, imageInstructions: event.target.value });
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.cms) {
      newErrors.cms = 'Please select a CMS preference';
    }
    
    if (formData.languages.length === 0) {
      newErrors.languages = 'Please select at least one language';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Website Requirements
      </Typography>

      {/* CMS Selection */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ExtensionIcon sx={{ mr: 1 }} />
          Content Management System
        </Typography>
        <FormControl component="fieldset" error={!!errors.cms} fullWidth>
          <FormLabel component="legend">Which CMS do you prefer?</FormLabel>
          <RadioGroup
            value={formData.cms}
            onChange={handleCmsChange}
            sx={{ mt: 1 }}
          >
            {cmsOptions.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={option.label}
              />
            ))}
          </RadioGroup>
          {errors.cms && <FormHelperText>{errors.cms}</FormHelperText>}
        </FormControl>
      </Paper>

      {/* Language Selection */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <LanguageIcon sx={{ mr: 1 }} />
          Languages
        </Typography>
        <FormControl fullWidth error={!!errors.languages}>
          <InputLabel>Which languages should be activated?</InputLabel>
          <Select
            multiple
            value={formData.languages}
            onChange={handleLanguageChange}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {languageOptions.map((language) => (
              <MenuItem key={language} value={language}>
                {language}
              </MenuItem>
            ))}
          </Select>
          {errors.languages && <FormHelperText>{errors.languages}</FormHelperText>}
        </FormControl>

        <FormControlLabel
          control={
            <Checkbox
              checked={formData.deeplIntegration}
              onChange={handleDeeplChange}
            />
          }
          label="Enable DeepL integration for automatic translations"
          sx={{ mt: 2 }}
        />
      </Paper>

      {/* Features Selection */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Website Features
        </Typography>
        <FormLabel component="legend" sx={{ mb: 2 }}>
          Which features does your website need to have?
        </FormLabel>
        <FormGroup>
          {featureOptions.map((feature) => (
            <FormControlLabel
              key={feature.key}
              control={
                <Checkbox
                  checked={formData.features[feature.key]}
                  onChange={handleFeatureChange(feature.key)}
                />
              }
              label={feature.label}
            />
          ))}
        </FormGroup>

        {/* Other Features */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Other Features
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Add custom feature..."
              value={newFeature}
              onChange={(e) => setNewFeature(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddOtherFeature();
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handleAddOtherFeature}
              startIcon={<AddIcon />}
            >
              Add
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {formData.otherFeatures.map((feature, index) => (
              <Chip
                key={index}
                label={feature}
                onDelete={() => handleRemoveOtherFeature(index)}
                deleteIcon={<CloseIcon />}
              />
            ))}
          </Box>
        </Box>
      </Paper>

      {/* Design Information */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PaletteIcon sx={{ mr: 1 }} />
          Design Information
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder="Provide design references, instructions, or describe the style you want..."
          value={formData.designInfo}
          onChange={handleDesignInfoChange}
          sx={{ mb: 2 }}
        />
        <Button
          variant="outlined"
          component="label"
          startIcon={<UploadIcon />}
        >
          Upload design files
          <input
            type="file"
            hidden
            multiple
            accept="image/*,.pdf,.sketch,.fig"
            onChange={handleFileUpload}
          />
        </Button>
        {formData.designFiles.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {formData.designFiles.length} file(s) uploaded
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Image Preferences */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ImageIcon sx={{ mr: 1 }} />
          Image Preferences
        </Typography>
        <FormControl component="fieldset">
          <RadioGroup
            value={formData.imagePreference}
            onChange={handleImagePreferenceChange}
          >
            <FormControlLabel
              value="upload"
              control={<Radio />}
              label="I will upload my own images"
            />
            <FormControlLabel
              value="ai-generated"
              control={<Radio />}
              label="Use AI to generate images"
            />
            <FormControlLabel
              value="free-stock"
              control={<Radio />}
              label="Search and use free stock images"
            />
          </RadioGroup>
        </FormControl>
        
        {(formData.imagePreference === 'ai-generated' || formData.imagePreference === 'free-stock') && (
          <TextField
            fullWidth
            multiline
            rows={2}
            placeholder="Describe the type of images you want..."
            value={formData.imageInstructions}
            onChange={handleImageInstructionsChange}
            sx={{ mt: 2 }}
          />
        )}
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit}>
          Continue with AI Assistant
        </Button>
      </Box>
    </Box>
  );
};

export default WebsiteRequirementsForm;